import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SparklesCore } from '../components/ui/sparkles';
import { ContainerScroll } from '../components/ui/container-scroll-animation';
import { AnimatedHero } from '../components/ui/animated-hero';
import MindTraceFooter from '../components/ui/mindtrace-footer';
import { CheckCircle, BarChart3, Users, Award, Sun, Moon, MoveRight } from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();
  const [darkMode, setDarkMode] = useState(true); // Default to dark mode

  // Apply dark mode class to document root
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'bg-black' : 'bg-white'}`} style={{ zoom: '0.95' }}>
      {/* Fixed Top Bar with Sign In Button */}
      <div className="fixed top-0 left-0 right-0 z-50 px-6 py-4 flex justify-between items-center">
        <button
          onClick={() => navigate('/login')}
          className="inline-flex items-center justify-center gap-4 h-11 rounded-md px-8 text-base font-semibold bg-blue-600 dark:bg-white text-white dark:text-black hover:bg-blue-700 dark:hover:bg-gray-200 transition-colors duration-300"
        >
          Get Started Free <MoveRight className="w-4 h-4" />
        </button>

        {/* Floating Dark Mode Toggle */}
        <button
          onClick={toggleDarkMode}
          className={`p-3 rounded-full transition-all shadow-lg ${darkMode
              ? 'bg-white/10 text-white hover:bg-white/20 backdrop-blur-md'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
        >
          {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
        </button>
      </div>

      {/* Hero Section with Sparkles */}
      <div className={`h-screen relative w-full flex flex-col items-center justify-center overflow-hidden transition-colors duration-300 ${darkMode ? 'bg-black' : 'bg-white'
        }`}>
        <h1 className={`md:text-7xl text-3xl lg:text-9xl font-bold text-center relative z-20 transition-colors duration-300 ${darkMode ? 'text-white' : 'text-gray-900'
          }`}>
          MindTrace
        </h1>
        <div className="w-[40rem] h-40 relative">
          {/* Gradients */}
          <div className={`absolute inset-x-20 top-0 h-[2px] w-3/4 blur-sm ${darkMode
              ? 'bg-gradient-to-r from-transparent via-white to-transparent'
              : 'bg-gradient-to-r from-transparent via-indigo-500 to-transparent'
            }`} />
          <div className={`absolute inset-x-20 top-0 h-px w-3/4 ${darkMode
              ? 'bg-gradient-to-r from-transparent via-white to-transparent'
              : 'bg-gradient-to-r from-transparent via-indigo-500 to-transparent'
            }`} />
          <div className={`absolute inset-x-60 top-0 h-[5px] w-1/4 blur-sm ${darkMode
              ? 'bg-gradient-to-r from-transparent via-gray-300 to-transparent'
              : 'bg-gradient-to-r from-transparent via-sky-500 to-transparent'
            }`} />
          <div className={`absolute inset-x-60 top-0 h-px w-1/4 ${darkMode
              ? 'bg-gradient-to-r from-transparent via-gray-300 to-transparent'
              : 'bg-gradient-to-r from-transparent via-sky-500 to-transparent'
            }`} />
          {/* Core component */}
          <SparklesCore
            background="transparent"
            minSize={0.4}
            maxSize={1}
            particleDensity={1200}
            className="w-full h-full"
            particleColor={darkMode ? "#FFFFFF" : "#000000"}
          />
          {/* Radial Gradient to prevent sharp edges */}
          <div className={`absolute inset-0 w-full h-full ${darkMode
              ? 'bg-black [mask-image:radial-gradient(350px_200px_at_top,transparent_20%,black)]'
              : 'bg-white [mask-image:radial-gradient(350px_200px_at_top,transparent_20%,white)]'
            }`}></div>
        </div>
        <p className={`text-xl mt-8 max-w-2xl text-center px-4 relative z-20 transition-colors duration-300 ${darkMode ? 'text-gray-300' : 'text-gray-600'
          }`}>
          Explainable Mentor Evaluation System
        </p>
      </div>

      {/* Scroll Animation Section */}
      <div className={`transition-colors duration-300 ${darkMode ? 'bg-black' : 'bg-white'}`}>
        <ContainerScroll
          titleComponent={
            <div className="text-center">
              <h2 className={`text-4xl md:text-6xl font-bold mb-4 transition-colors duration-300 ${darkMode ? 'text-white' : 'text-gray-900'
                }`}>
                Unleash the power of
              </h2>
              <h2 className={`text-5xl md:text-7xl lg:text-8xl font-bold bg-clip-text text-transparent transition-colors duration-300 ${darkMode
                  ? 'bg-gradient-to-b from-white to-gray-400'
                  : 'bg-gradient-to-b from-gray-900 to-gray-600'
                }`}>
                AI-Powered Evaluation
              </h2>
            </div>
          }
        >
          <img
            src="/landing-dashboard-preview.png"
            alt="dashboard preview"
            className="mx-auto rounded-2xl object-cover h-full w-full"
            draggable={false}
          />
        </ContainerScroll>
      </div>

      {/* Features Section */}
      <div id="features" className={`py-20 px-4 transition-colors duration-300 ${darkMode ? 'bg-black' : 'bg-gray-50'
        }`}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className={`text-4xl md:text-5xl font-bold mb-4 transition-colors duration-300 ${darkMode ? 'text-white' : 'text-gray-900'
              }`}>
              Powerful Features for Better Teaching
            </h2>
            <p className={`text-xl transition-colors duration-300 ${darkMode ? 'text-gray-300' : 'text-gray-600'
              }`}>
              Everything you need to evaluate and improve teaching quality
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className={`rounded-xl p-8 hover:shadow-xl transition-all duration-300 ${darkMode
                ? 'bg-white/5 border border-white/10 hover:border-white/20'
                : 'bg-white border border-gray-200'
              }`}>
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-6 ${darkMode ? 'bg-white/10' : 'bg-blue-100'
                }`}>
                <BarChart3 className={`w-6 h-6 ${darkMode ? 'text-white' : 'text-blue-600'}`} />
              </div>
              <h3 className={`text-2xl font-bold mb-4 transition-colors duration-300 ${darkMode ? 'text-white' : 'text-gray-900'
                }`}>
                AI-Powered Analysis
              </h3>
              <p className={`mb-4 transition-colors duration-300 ${darkMode ? 'text-gray-300' : 'text-gray-600'
                }`}>
                Advanced LLM technology analyzes teaching sessions across multiple dimensions including clarity, structure, correctness, pacing, and communication.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <CheckCircle className={`w-5 h-5 mr-2 mt-0.5 flex-shrink-0 ${darkMode ? 'text-green-400' : 'text-green-600'
                    }`} />
                  <span className={`transition-colors duration-300 ${darkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                    Segment-by-segment evaluation
                  </span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className={`w-5 h-5 mr-2 mt-0.5 flex-shrink-0 ${darkMode ? 'text-green-400' : 'text-green-600'
                    }`} />
                  <span className={`transition-colors duration-300 ${darkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                    Detailed scoring metrics
                  </span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className={`w-5 h-5 mr-2 mt-0.5 flex-shrink-0 ${darkMode ? 'text-green-400' : 'text-green-600'
                    }`} />
                  <span className={`transition-colors duration-300 ${darkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                    Explainable feedback
                  </span>
                </li>
              </ul>
            </div>

            {/* Feature 2 */}
            <div className={`rounded-xl p-8 hover:shadow-xl transition-all duration-300 ${darkMode
                ? 'bg-white/5 border border-white/10 hover:border-white/20'
                : 'bg-white border border-gray-200'
              }`}>
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-6 ${darkMode ? 'bg-white/10' : 'bg-purple-100'
                }`}>
                <Users className={`w-6 h-6 ${darkMode ? 'text-white' : 'text-purple-600'}`} />
              </div>
              <h3 className={`text-2xl font-bold mb-4 transition-colors duration-300 ${darkMode ? 'text-white' : 'text-gray-900'
                }`}>
                Mentor Management
              </h3>
              <p className={`mb-4 transition-colors duration-300 ${darkMode ? 'text-gray-300' : 'text-gray-600'
                }`}>
                Comprehensive dashboard to manage mentors, track their performance over time, and identify trends in teaching quality.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <CheckCircle className={`w-5 h-5 mr-2 mt-0.5 flex-shrink-0 ${darkMode ? 'text-green-400' : 'text-green-600'
                    }`} />
                  <span className={`transition-colors duration-300 ${darkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                    Performance tracking
                  </span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className={`w-5 h-5 mr-2 mt-0.5 flex-shrink-0 ${darkMode ? 'text-green-400' : 'text-green-600'
                    }`} />
                  <span className={`transition-colors duration-300 ${darkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                    Trend analysis
                  </span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className={`w-5 h-5 mr-2 mt-0.5 flex-shrink-0 ${darkMode ? 'text-green-400' : 'text-green-600'
                    }`} />
                  <span className={`transition-colors duration-300 ${darkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                    Individual insights
                  </span>
                </li>
              </ul>
            </div>

            {/* Feature 3 */}
            <div className={`rounded-xl p-8 hover:shadow-xl transition-all duration-300 ${darkMode
                ? 'bg-white/5 border border-white/10 hover:border-white/20'
                : 'bg-white border border-gray-200'
              }`}>
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-6 ${darkMode ? 'bg-white/10' : 'bg-green-100'
                }`}>
                <Award className={`w-6 h-6 ${darkMode ? 'text-white' : 'text-green-600'}`} />
              </div>
              <h3 className={`text-2xl font-bold mb-4 transition-colors duration-300 ${darkMode ? 'text-white' : 'text-gray-900'
                }`}>
                Visual Insights
              </h3>
              <p className={`mb-4 transition-colors duration-300 ${darkMode ? 'text-gray-300' : 'text-gray-600'
                }`}>
                Interactive visualizations help you understand teaching flow, identify strengths, and pinpoint areas for improvement at a glance.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <CheckCircle className={`w-5 h-5 mr-2 mt-0.5 flex-shrink-0 ${darkMode ? 'text-green-400' : 'text-green-600'
                    }`} />
                  <span className={`transition-colors duration-300 ${darkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                    Flow visualizations
                  </span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className={`w-5 h-5 mr-2 mt-0.5 flex-shrink-0 ${darkMode ? 'text-green-400' : 'text-green-600'
                    }`} />
                  <span className={`transition-colors duration-300 ${darkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                    Performance charts
                  </span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className={`w-5 h-5 mr-2 mt-0.5 flex-shrink-0 ${darkMode ? 'text-green-400' : 'text-green-600'
                    }`} />
                  <span className={`transition-colors duration-300 ${darkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                    Actionable feedback
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* How it Works Section */}
      <div className={`py-20 px-4 transition-colors duration-300 ${darkMode ? 'bg-black' : 'bg-white'
        }`}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className={`text-4xl md:text-5xl font-bold mb-4 transition-colors duration-300 ${darkMode ? 'text-white' : 'text-gray-900'
              }`}>
              How MindTrace Works
            </h2>
            <p className={`text-xl transition-colors duration-300 ${darkMode ? 'text-gray-300' : 'text-gray-600'
              }`}>
              Simple workflow, powerful results
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold transition-colors duration-300 ${darkMode ? 'bg-white text-black' : 'bg-blue-600 text-white'
                }`}>
                1
              </div>
              <h3 className={`text-xl font-bold mb-2 transition-colors duration-300 ${darkMode ? 'text-white' : 'text-gray-900'
                }`}>
                Upload Video
              </h3>
              <p className={`transition-colors duration-300 ${darkMode ? 'text-gray-300' : 'text-gray-600'
                }`}>
                Upload your teaching session video to the platform
              </p>
            </div>

            <div className="text-center">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold transition-colors duration-300 ${darkMode ? 'bg-white text-black' : 'bg-purple-600 text-white'
                }`}>
                2
              </div>
              <h3 className={`text-xl font-bold mb-2 transition-colors duration-300 ${darkMode ? 'text-white' : 'text-gray-900'
                }`}>
                AI Transcription
              </h3>
              <p className={`transition-colors duration-300 ${darkMode ? 'text-gray-300' : 'text-gray-600'
                }`}>
                Automatic transcription with timestamp precision
              </p>
            </div>

            <div className="text-center">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold transition-colors duration-300 ${darkMode ? 'bg-white text-black' : 'bg-green-600 text-white'
                }`}>
                3
              </div>
              <h3 className={`text-xl font-bold mb-2 transition-colors duration-300 ${darkMode ? 'text-white' : 'text-gray-900'
                }`}>
                Smart Analysis
              </h3>
              <p className={`transition-colors duration-300 ${darkMode ? 'text-gray-300' : 'text-gray-600'
                }`}>
                AI evaluates teaching quality across multiple metrics
              </p>
            </div>

            <div className="text-center">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold transition-colors duration-300 ${darkMode ? 'bg-white text-black' : 'bg-yellow-500 text-white'
                }`}>
                4
              </div>
              <h3 className={`text-xl font-bold mb-2 transition-colors duration-300 ${darkMode ? 'text-white' : 'text-gray-900'
                }`}>
                Get Insights
              </h3>
              <p className={`transition-colors duration-300 ${darkMode ? 'text-gray-300' : 'text-gray-600'
                }`}>
                Receive detailed feedback and actionable recommendations
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section - Animated Hero */}
      <div className={darkMode ? 'dark' : ''}>
        <AnimatedHero />
      </div>

      {/* Footer */}
      <div className={darkMode ? 'dark' : ''}>
        <MindTraceFooter />
      </div>
    </div>
  );
};

export default LandingPage;